import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import Select from 'react-select';

import { Box, Button, TextInput } from '@qwealth/qcore';
import {
  Partner,
  selectPartners,
  selectUser,
  selectUserIsAdmin,
  selectUserIsAdvisor,
  selectUserIsAdvisorSupport,
  selectUserIsCompliance,
  toOptions,
} from '@qwealth/qdata';
import { MaskedDateField } from '@qwealth/kyc';

import { selectClientsComplianceReview, selectComplianceAssignees } from 'data/selectors/compliance';
import { useAppSelector } from 'data/store';
import {
  ClientComplianceReviewFilters,
  COMPLIANCE_CHECKLIST_ACTOR,
  COMPLIANCE_CHECKLIST_STATUSES_LIMITED,
  ComplianceChecklistActor,
  ComplianceChecklistStatusLimited,
} from 'data/models/Compliance';
import { initialFilters } from 'data/helpers/compliance';
import { startEndDateValidator } from 'data/validations/dateValidation';
import { baseSelectStyles } from 'components/common/BaseSelect';

interface Props {
  searchQuery: string;
  setSearchQuery: Dispatch<SetStateAction<string>>;
  mutableFilters: ClientComplianceReviewFilters;
  setMutableFilters: Dispatch<SetStateAction<ClientComplianceReviewFilters>>;
}

const ComplianceReviewsHeader: FC<Props> = ({ searchQuery, setSearchQuery, mutableFilters, setMutableFilters }): JSX.Element => {
  const complianceAssignees = useAppSelector(selectComplianceAssignees);
  const clientsToReview = useAppSelector(selectClientsComplianceReview);
  const isUserAdmin = useAppSelector(selectUserIsAdmin);
  const isUserCompliance = useAppSelector(selectUserIsCompliance);
  const isUserAdvisor = useAppSelector(selectUserIsAdvisor);
  const isUserAdvisorSupport = useAppSelector(selectUserIsAdvisorSupport);
  const partnerList: Partner[] = useAppSelector(selectPartners);
  const user = useAppSelector(selectUser);

  const ipsDateError = startEndDateValidator(mutableFilters.ipsDateStart, mutableFilters.ipsDateEnd);

  const [partnerFilterOptions, setPartnerFilterOptions] = useState<string[]>([]);

  useEffect(() => {
    if (isUserAdvisor || isUserAdvisorSupport) {
      const userPartner: Partner | undefined = partnerList.find(partner => partner.id === user.partnerID);
      !userPartner ? setPartnerFilterOptions([]) : setPartnerFilterOptions([userPartner.partnerName]);
    } else {
      setPartnerFilterOptions(partnerList.map(partner => partner.partnerName));
    }
  }, [isUserAdmin, isUserCompliance, partnerList, user]);

  const handleResetFilters = () => {
    setMutableFilters({ ...initialFilters, ipsDateStart: undefined, ipsDateEnd: undefined });
    setSearchQuery('');
  };

  return (
    <Box display="flex" flexDirection="column" gap="default" marginBottom="large">
      <Box display="flex" flexDirection="column">
        <TextInput
          placeholder="Search Checklists by Household QID and Name, Client QID and Name, Reviewer, IA Code"
          data-testid="query-input"
          onChange={e => {
            setSearchQuery(e.target.value ?? '');
          }}
          value={searchQuery}
        />
      </Box>

      <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(250px, 1fr))" gap="default">
        <Box display="flex" flexDirection="column">
          <Box textStyle="formLabel">Reviewer</Box>
          <Select
            placeholder="Select Reviewer"
            styles={baseSelectStyles()}
            value={mutableFilters.assignee ? toOptions<string>([mutableFilters.assignee]) : null}
            onChange={options => {
              setMutableFilters(prev => ({ ...prev, assignee: options ? options.value : null }));
            }}
            options={toOptions<string>(complianceAssignees)}
            isClearable
          />
        </Box>

        <Box display="flex" flexDirection="column">
          <Box textStyle="formLabel">Partner</Box>
          <Select
            placeholder="Select Partner"
            styles={baseSelectStyles()}
            value={mutableFilters.partnerName ? toOptions<string>([mutableFilters.partnerName]) : null}
            onChange={options => {
              setMutableFilters(prev => ({ ...prev, partnerName: options ? options.value : null }));
            }}
            options={toOptions<string>(partnerFilterOptions)}
            isClearable
          />
        </Box>

        <Box display="flex" flexDirection="column">
          <Box textStyle="formLabel">Checklist Status</Box>
          <Select
            placeholder="Select Checklist Status"
            styles={baseSelectStyles()}
            value={
              mutableFilters.complianceChecklistStatus
                ? toOptions<ComplianceChecklistStatusLimited>([mutableFilters.complianceChecklistStatus])
                : null
            }
            onChange={options => {
              setMutableFilters(prev => ({ ...prev, complianceChecklistStatus: options ? options.value : null }));
            }}
            options={toOptions<ComplianceChecklistStatusLimited>([...COMPLIANCE_CHECKLIST_STATUSES_LIMITED])}
            isClearable
          />
        </Box>

        <Box display="flex" flexDirection="column">
          <Box textStyle="formLabel">Action Required By</Box>
          <Select
            placeholder="Select Actor"
            styles={baseSelectStyles()}
            value={
              mutableFilters.responsibleComplianceActors ? toOptions<ComplianceChecklistActor>([mutableFilters.responsibleComplianceActors]) : null
            }
            onChange={options => {
              setMutableFilters(prev => ({ ...prev, responsibleComplianceActors: options ? options.value : null }));
            }}
            options={toOptions<ComplianceChecklistActor>([...COMPLIANCE_CHECKLIST_ACTOR])}
            isClearable
          />
        </Box>

        <Box>
          <Box display="flex" flexDirection="row" gap="default" justifyContent="space-between">
            <Box display="flex" flexDirection="column" width="50%">
              <Box textStyle="formLabel">IPS Date Starting</Box>
              <MaskedDateField
                value={mutableFilters.ipsDateStart}
                onChange={date => {
                  setMutableFilters(prev => ({ ...prev, ipsDateStart: date || undefined }));
                }}
              />
            </Box>
            <Box display="flex" flexDirection="column" width="50%">
              <Box textStyle="formLabel">IPS Date Ending</Box>
              <MaskedDateField
                key={mutableFilters.ipsDateEnd}
                value={mutableFilters.ipsDateEnd}
                onChange={date => {
                  setMutableFilters(prev => ({ ...prev, ipsDateEnd: date || undefined }));
                }}
              />
            </Box>
          </Box>
          {ipsDateError && (
            <Box color="error" display="flex">
              {ipsDateError}
            </Box>
          )}
        </Box>
      </Box>
      {clientsToReview && (
        <Box display="flex" flexDirection="column" gap="small">
          <Box>
            You have a <strong>total of {clientsToReview.length} checklists</strong> assigned to your team.
          </Box>
          <Box fontStyle="italic">
            <strong>Note:</strong> If you do not see checklists that you expect to see, please adjust the filters above or click on reset filters.
          </Box>
          <Box display="flex" justifyContent="flex-start" alignItems="flex-end">
            <Button onClick={handleResetFilters}>Reset Filters</Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ComplianceReviewsHeader;
